import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Tabs as TabsComponent, TabPanel } from "@/components/Tabs/Tabs";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { UserLanguage } from "@/enum/user.enum";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { api } from "@/redux/api";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

import { ProfileCard } from "./components/ProfileCard";
import { CopywritingSettingsCard } from "./components/CopywritingSettingsCard";
import { ConnectedAccounts } from "./components/ConnectedAccounts";
import { LinkedinConnections } from "./components/LinkedinConnections";
import { NotificationPreferences } from "./components/NotificationPreferences";
import { RECONNECT_LINKEDIN_PARAM, RECONNECT_LINKEDIN_VALUE } from "./components/LinkedinLogin";
import { AccountTabs as Tabs, isAccountTab as isTab } from "./accountTabs";
import { FeaturesCodes } from "../Users/enum/codes";

export const Account: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const [searchParams, setSearchParams] = useSearchParams();

	const dispatch = useAppDispatch();
	const isNotificationCenterActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);

	// A LinkedIn reconnect deep-link (`?reconnect=linkedin`) should land on the tab that
	// hosts the LinkedIn login card so its reconnect modal can open.
	const wantsLinkedinReconnect =
		searchParams.get(RECONNECT_LINKEDIN_PARAM) === RECONNECT_LINKEDIN_VALUE;

	const resolveInitialTab = (): Tabs => {
		const requested = searchParams.get("tab");

		if (isTab(requested)) {
			return requested;
		}

		return wantsLinkedinReconnect ? Tabs.CONNECTED_ACCOUNTS : Tabs.GENERAL;
	};

	const [currentTab, setCurrentTab] = useState<Tabs>(resolveInitialTab);

	useEffect(() => {
		dispatch(api.util.invalidateTags(["Me"]));
	}, []);

	useEffect(() => {
		if (searchParams.has("tab") && isTab(searchParams.get("tab"))) {
			setCurrentTab(searchParams.get("tab") as Tabs);
		}
	}, [searchParams]);

	useEffect(() => {
		const requested = searchParams.get("tab");

		if (!isTab(requested) && requested !== currentTab) {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("tab", currentTab);

					return next;
				},
				{ replace: true }
			);
		}
	}, [searchParams]);

	const { user } = useAppSelector(authSelector);

	const tabs = [
		{ label: ts("tabs.general"), value: Tabs.GENERAL },
		{ label: ts("tabs.connectedAccounts"), value: Tabs.CONNECTED_ACCOUNTS },
		{ label: ts("tabs.linkedinConnections"), value: Tabs.LINKEDIN_CONNECTIONS },
		...(isNotificationCenterActive
			? [{ label: ts("tabs.notifications"), value: Tabs.NOTIFICATIONS }]
			: []),
	];

	const handleTabsChange = useCallback(
		(value: string): void => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("tab", value);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	return (
		// Full-bleed page shell: a white header (title bar + tab strip) over a brand-white
		// content area, so the white cards/forms stand out against the page background. The
		// negative margins cancel MainLayout's `px-4 pt-3 pb-4` padding so this background spans
		// the full width — scoped to this page only, leaving MainLayout and every other page
		// untouched. `min-h` keeps the brand-white filling the viewport when content is short.
		<div className="-mx-4 -mt-3 -mb-4 min-h-[calc(100vh-1rem)] bg-brand-white">
			<div className="bg-white">
				<div className="flex h-12 items-center border-b border-border-light px-4">
					<span className="whitespace-nowrap text-sm font-semibold text-text">{ts("title")}</span>

					<div className="ml-auto flex items-center gap-2">
						<HelpLauncher />
					</div>
				</div>

				<TabsComponent
					activeTab={currentTab}
					ariaLabel="Account tabs"
					tabs={tabs}
					onTabChange={handleTabsChange}
				/>
			</div>

			<div className="px-4 py-4">
				<TabPanel activeTab={currentTab} value={Tabs.GENERAL}>
					<div className="flex flex-col gap-4">
						<ProfileCard
							email={user?.email ?? ""}
							firstName={user?.firstName ?? ""}
							is2FAEnabled={user?.is2FAEnabled || false}
							lastName={user?.lastName ?? ""}
							linkedinUrl={user?.linkedinUrl ?? ""}
							userLanguage={user?.userLanguage ?? UserLanguage.EN}
						/>

						<CopywritingSettingsCard />
					</div>
				</TabPanel>

				<TabPanel activeTab={currentTab} value={Tabs.CONNECTED_ACCOUNTS}>
					<ConnectedAccounts />
				</TabPanel>

				<TabPanel activeTab={currentTab} value={Tabs.LINKEDIN_CONNECTIONS}>
					<div className="flex flex-col gap-4">
						<LinkedinConnections amount={user?.prospects?.length || 0} />
					</div>
				</TabPanel>

				{isNotificationCenterActive && (
					<TabPanel activeTab={currentTab} value={Tabs.NOTIFICATIONS}>
						<NotificationPreferences />
					</TabPanel>
				)}
			</div>
		</div>
	);
};
